import { useMemo } from 'react';

import {
  ConsolidatedGraph,
  GraphEntityType,
} from '@/components/nodeGraph/utils/types';
import { Asset, ResourceType, Seed, Technology, TreeNode } from '@/types';

import {
  addADObjectsToGraph,
  addADRelationshipsToGraph,
  addAssetsToGraph,
  addRiskAssetsToGraph,
  addRisksToGraph,
  addSeedsToGraph,
  addTechnologiesToGraph,
  addWebpagesToGraph,
} from './transformations';

/**
 * Hook parameters for building the consolidated graph.
 */
interface UseConsolidatedGraphParams {
  /** Array of primary assets to include in the graph */
  assets: Asset[];
  /** Relationship data from useRelationships hook */
  relationships: {
    assetToSeedData?: TreeNode[];
    assetToRiskData?: TreeNode[];
    riskToAssetData?: TreeNode[];
    technologiesData?: TreeNode[];
    webpagesData?: TreeNode[];
    risksTagsData?: TreeNode[];
    assetTagsData?: TreeNode[];
    adObjectsData?: TreeNode[];
  };
  /** Entity type to cluster the graph by */
  clusterByEntity?: GraphEntityType;
}

/**
 * Custom hook for building the consolidated graph from assets and relationship data.
 * Merges all entities into a single graph structure based on the clustering strategy.
 *
 * @param params - Parameters including assets, relationships, and clustering configuration
 * @returns Object containing the consolidated graph and extracted entities
 */
export const useConsolidatedGraph = ({
  assets,
  relationships,
  clusterByEntity,
}: UseConsolidatedGraphParams) => {
  const {
    assetToSeedData,
    assetToRiskData,
    riskToAssetData,
    technologiesData,
    webpagesData,
    adObjectsData,
  } = relationships;

  // Process allAssets to include discovered assets from risks
  const allAssets = useMemo(() => {
    const discoveredAssets: Asset[] = [];
    (riskToAssetData || []).forEach(resp => {
      const asset = resp.node.result?.assets?.[0];
      if (asset) {
        discoveredAssets.push(asset);
      }
    });

    const assetMap = new Map<string, Asset>();

    // Add original assets first
    assets.forEach((asset: Asset) => {
      assetMap.set(asset.key, asset);
    });

    // Add discovered assets (won't overwrite existing ones)
    discoveredAssets.forEach(asset => {
      if (!assetMap.has(asset.key)) {
        assetMap.set(asset.key, asset);
      }
    });

    return Array.from(assetMap.values());
  }, [assets, riskToAssetData]);

  // Create consolidated graph object directly from API responses
  const consolidatedGraph: ConsolidatedGraph = useMemo(() => {
    const graph: ConsolidatedGraph = {};

    // Build graph based on clustering entity type
    if (clusterByEntity === 'SharpHound') {
      // Build SharpHound graph with AD objects and their relationships
      addADObjectsToGraph(graph, adObjectsData || []);
      addADRelationshipsToGraph(graph, adObjectsData || []);
    } else {
      // Build regular graph with assets and related entities
      addAssetsToGraph(graph, allAssets);
      addSeedsToGraph(graph, assetToSeedData || []);
      addRisksToGraph(graph, assetToRiskData || []);
      addRiskAssetsToGraph(graph, riskToAssetData || []);
      addTechnologiesToGraph(graph, technologiesData || []);
      addWebpagesToGraph(graph, webpagesData || []);
    }

    return graph;
  }, [
    clusterByEntity,
    allAssets,
    assetToSeedData,
    assetToRiskData,
    riskToAssetData,
    technologiesData,
    webpagesData,
    adObjectsData,
  ]);

  // Extract entities from consolidated graph for legacy compatibility
  const seeds = useMemo(() => {
    return Object.values(consolidatedGraph || {})
      .filter(node => node.type === ResourceType.Seed)
      .map(node => node.data as Seed);
  }, [consolidatedGraph]);

  const technologies = useMemo(() => {
    return Object.values(consolidatedGraph || {})
      .filter(node => node.type === ResourceType.Technology)
      .map(node => node.data as Technology);
  }, [consolidatedGraph]);

  return {
    /** The consolidated graph with all nodes and relationships */
    consolidatedGraph,
    /** All assets including discovered assets from risks */
    allAssets,
    /** Extracted seed entities from the graph */
    seeds,
    /** Extracted technology entities from the graph */
    technologies,
  };
};
